import { useEffect, useState } from 'react';
// LocalizedLink replaces react-router Link below
import { X, Sparkles } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import LocalizedLink from '@/components/LocalizedLink';

const DISMISS_KEY = 'bf-exit-intent-dismissed';
const COOLDOWN_HOURS = 24;

/**
 * Top-of-viewport exit-intent capture. Fires once per 24 hours per
 * visitor, only on desktop (mouse leaves the top edge), and stays
 * dismissible. Routes to /contact rather than collecting email
 * inline so we don't need to stand up a new lead endpoint just for
 * this surface — the existing contact funnel already handles PIPEDA
 * consent and delivery.
 */
const ExitIntentCapture = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Respect prior dismissal with a cooldown.
    try {
      const raw = window.localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const dismissedAt = Number(raw);
        const hours = (Date.now() - dismissedAt) / (1000 * 60 * 60);
        if (hours < COOLDOWN_HOURS) return;
      }
    } catch {
      /* ignore corrupted storage */
    }

    // Skip on touch devices — "exit intent" via mouseleave is
    // meaningless there and the interruption is particularly annoying.
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    if (isTouch) return;

    // Honor reduced-motion users — they opted out of interruptive UI.
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const handleMouseLeave = (e: MouseEvent) => {
      // Fire only when the pointer exits through the top of the viewport
      // (cursor heading for the tab bar / address bar) and only after the
      // page has had time to settle.
      if (e.clientY > 0) return;
      setVisible(true);
      trackEvent('exit_intent_shown', { page_path: window.location.pathname });
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 8000);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const onCtaClick = () => {
    trackEvent('exit_intent_cta', { page_path: window.location.pathname });
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-heading"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-border bg-background p-6 md:p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]">
        <div className="flex items-start justify-between gap-3">
          <span className="quantum-badge">
            <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            BEFORE YOU GO
          </span>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="-mr-2 -mt-2 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <h2 id="exit-intent-heading" className="mt-5 text-2xl font-light tracking-tight text-foreground">
          Not ready to book an audit?
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground/80">
          Take our free <span className="text-foreground font-medium">2-minute AI Readiness Assessment</span>.
          You'll get a tailored score and next steps — no email required.
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <LocalizedLink
            to="/ai-readiness"
            onClick={onCtaClick}
            className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-[0_0_25px_hsl(var(--primary)/0.5)]"
          >
            Start the Assessment
          </LocalizedLink>
          <button
            type="button"
            onClick={dismiss}
            className="inline-flex items-center justify-center rounded-lg border border-border bg-muted px-5 py-3 text-sm font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground"
          >
            No thanks
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentCapture;
